import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import { QuantitySelector } from '../QuantitySelector/QuantitySelector';

declare global {
    interface Window {

        // eslint-disable-next-line @typescript-eslint/naming-convention
        AddToSmartWishlist?: {
            (productId: number, variantId: number): string;
        };
    }
}

@Component
export class CartItemRow extends ThemeComponent {
    @Prop item!: Shopify.Liquid.LineItem;

    @Ref({ required: false }) quantitySelector!: QuantitySelector;
    @Ref({ required: false }) removeItemButton!: HTMLButtonElement[];
    @Ref({ required: false }) addToWishlistButton!: HTMLButtonElement[];
    @Ref({ required: false }) toggleActionsDrawerButton!: HTMLButtonElement;
    @Ref({ required: false }) actionsDrawer!: HTMLElement;
    @Ref({ required: false }) closeActionsDrawerButton!: HTMLButtonElement;

    init() {

        // Listen for item remove clicks
        for (const button of this.removeItemButton) {
            this.$listen(button, 'click', () => this.handleQuantity(0));
        }

        // Subscribe to quantity change
        if (this.quantitySelector) {
            this.$subscribe(this.quantitySelector.onChange, (quantity) => {
                this.handleQuantity(quantity).catch((e) => {
                    this.$log.error(`Update quantity failed`, { e });
                });
            });
        }

        // Listen for toggle actions drawer button clicks
        this.listenForToggleActionsDrawerButtonClicks();

        // Listen for outer actions drawer click
        this.listenForOuterActionsDrawerClick();

        // Listen for close actions drawer button click
        this.listenForCloseActionsDrawerButtonClick();

        // Listen for add to wishlist button click
        this.listenForAddToWishlistButtonClick();
    }

    async handleQuantity(quantity: number) {

        // Find matching cart item
        let matchingItem = null;
        for (const item of cart.items) {
            if (item.variantId === this.item.id) {
                matchingItem = item;
            }
        }
        if (matchingItem === null) {
            this.$log.error(`No matching item found`);
            return;
        }

        // Attemp quantity update
        try {
            if (quantity === 0) {

                // Remove item
                await cart.removeItem(matchingItem);
            } else {

                // Change item
                await cart.changeItem(matchingItem, quantity, null);
            }
        } catch (event: unknown) {
            const { message } = event as Error;
            this.$log.error('Cart item quantity update error', message);
        }

        // Dispatch event for other components to listen to, including the cart line ID
        const cartChangedEvent = new CustomEvent('cartChanged', {
            detail: { cartLineId: matchingItem.id }
        });
        document.dispatchEvent(cartChangedEvent);
    }

    listenForToggleActionsDrawerButtonClicks() {
        if (!this.toggleActionsDrawerButton) {
            return;
        }
        if (!this.actionsDrawer) {
            return;
        }
        this.$listen(this.toggleActionsDrawerButton, 'click', () => {
            this.actionsDrawer.classList.add('cart-item-row__actions-drawer--active');
        });
    }

    listenForOuterActionsDrawerClick() {
        if (!this.actionsDrawer) {
            return;
        }
        this.$listen(this, 'click', (e) => {
            if (e.target) {
                const clickedElement = e.target as HTMLElement;
                if (clickedElement === this.actionsDrawer) {
                    this.actionsDrawer.classList.remove('cart-item-row__actions-drawer--active');
                }
            }
        });
    }

    listenForCloseActionsDrawerButtonClick() {
        if (!this.actionsDrawer) {
            return;
        }
        if (!this.closeActionsDrawerButton) {
            return;
        }
        this.$listen(this.closeActionsDrawerButton, 'click', () => {
            this.actionsDrawer.classList.remove('cart-item-row__actions-drawer--active');
        });
    }

    listenForAddToWishlistButtonClick() {

        // Add click event listeners
        for (const wishlistButton of this.addToWishlistButton) {
            this.$listen(wishlistButton, 'click', () => {

                // Get product and variant ID's
                let productId: string|number|null = wishlistButton.getAttribute('data-product-id');
                let variantId: string|number|null = wishlistButton.getAttribute('data-variant-id');
                if (productId === null || variantId === null) {
                    return;
                }
                productId = Number(productId);
                variantId = Number(variantId);

                // Check if function is defined
                if (typeof window.AddToSmartWishlist !== 'undefined') {

                    // Make wishlist API call
                    window.AddToSmartWishlist(productId, variantId);

                    // Remove item from cart
                    void this.handleQuantity(0);
                } else {
                    this.$log.warn('window.AddToSmartWishlist is undefined');
                }
            });
        }
    }
}
